import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import { Link as RouterLink } from 'react-router-dom';

export interface BuilderHeaderProps {
  /** Colours the strip along the top edge. */
  isActive?: boolean | null;
  backTo: string;
  backLabel: string;
  /** The editable name, usually an `InlineNameField`. */
  name: React.ReactNode;
  /** The id line beside the name, e.g. "ID 12190". */
  identifier?: React.ReactNode;
  /** The Active/Inactive control. */
  status?: React.ReactNode;
  /** Buttons at the right end of the first row. */
  actions?: React.ReactNode;
  /** The second row: last published, save errors. */
  meta?: React.ReactNode;
}

/**
 * The stack builder's header: two slim rows instead of the detail header's three-row block, so the
 * workspace gets the height back. Row one is identity and actions; row two is status text.
 */
export default function BuilderHeader({
  isActive,
  backTo,
  backLabel,
  name,
  identifier,
  status,
  actions,
  meta,
}: BuilderHeaderProps) {
  return (
    <Paper
      component='header'
      square
      elevation={0}
      sx={{ flex: '0 0 auto', borderBottom: 1, borderColor: 'divider', boxShadow: '0 1px 3px rgba(15, 23, 42, 0.12)' }}
    >
      <Box
        role='status'
        aria-label={isActive ? 'Active' : 'Inactive'}
        sx={{ height: 3, bgcolor: isActive ? 'success.main' : 'error.main' }}
      />
      <Stack spacing={0.25} sx={{ px: 1.5, pt: 0.75, pb: 0.75 }}>
        <Stack direction='row' spacing={1.5} useFlexGap sx={{ alignItems: 'center', minHeight: 40, flexWrap: 'wrap' }}>
          <Button component={RouterLink} to={backTo} size='small' startIcon={<ChevronLeft />} sx={{ flex: '0 0 auto' }}>
            {backLabel}
          </Button>
          <Divider orientation='vertical' flexItem sx={{ my: 1 }} />
          <Stack direction='row' spacing={1.5} useFlexGap sx={{ alignItems: 'center', flex: '1 1 320px', minWidth: 0 }}>
            {status && <Box sx={{ flex: '0 0 auto' }}>{status}</Box>}
            <Box sx={{ flex: '0 1 440px', minWidth: 160 }}>{name}</Box>
            {identifier && (
              <Typography variant='body2' color='text.secondary' noWrap sx={{ flex: '0 0 auto' }}>
                {identifier}
              </Typography>
            )}
          </Stack>
          {actions && (
            <Stack
              direction='row'
              spacing={0.75}
              useFlexGap
              sx={{ alignItems: 'center', ml: 'auto', flex: '0 0 auto' }}
            >
              {actions}
            </Stack>
          )}
        </Stack>
        {meta && (
          <Box sx={{ pl: 1, minHeight: 20, display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>{meta}</Box>
        )}
      </Stack>
    </Paper>
  );
}
